/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero — Totals, tax and change calculation (PURE LOGIC)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: this file is PURE `.ts`. Does NOT import lingui macros (`msg`/`t`) or
 * no UI. Takes numbers (cents / basis points) and returns numbers.
 * Translated strings provided by consuming `.tsx`.
 *
 * Convenciones:
 *   - All amounts are integers in minor units (cents).
 *   - `tax_rate` is expressed in basis points: 1600 = 16%.
 *   - VAT handled as ADDED (not included) for MVP.
 */

const BASIS_POINTS = 10_000;

export interface CalcModifier {
  /** Price delta in cents (can be negative). */
  priceDelta: number;
}

export interface CalcLine {
  /** Base unit price of product, in cents. */
  unitPrice: number;
  /** Cantidad (entero ≥ 1). */
  qty: number;
  /** Tasa de impuesto en basis points (1600 = 16%). */
  taxRate?: number;
  /** Modifiers applied to line. */
  modifiers?: CalcModifier[];
}

export interface OrderTotals {
  /** Line sum (base + modifiers) × qty, before tax and discount. */
  subtotal: number;
  /** Descuento aplicado a nivel ticket, en centavos. */
  discount: number;
  /** Impuesto total, en centavos. */
  taxTotal: number;
  /** Total a cobrar = subtotal − descuento + impuesto. */
  total: number;
}

/** Effective price of line unit (base + sum of modifiers). */
export function lineUnitEffectivePrice(line: CalcLine): number {
  const mods = line.modifiers ?? [];
  const modSum = mods.reduce((acc, m) => acc + m.priceDelta, 0);
  return line.unitPrice + modSum;
}

/** Line total (effective price × quantity), before tax. */
export function lineTotal(line: CalcLine): number {
  const qty = Math.max(0, Math.trunc(line.qty));
  return lineUnitEffectivePrice(line) * qty;
}

/**
 * Calculates order totals from its lines.
 *
 * Ticket discount is distributed proportionally over taxable base
 * so tax is calculated over discounted net (common model in
 * LatAm POS). Tax rounded per line to prevent discrepancies.
 * 1 centavo en el arqueo.
 */
export function computeOrderTotals(lines: CalcLine[], discount = 0): OrderTotals {
  const subtotal = lines.reduce((acc, l) => acc + lineTotal(l), 0);
  const safeDiscount = clampDiscount(discount, subtotal);

  // Proportional discount factor applied to each line.
  const netFactor = subtotal > 0 ? (subtotal - safeDiscount) / subtotal : 0;

  let taxTotal = 0;
  for (const line of lines) {
    const rate = line.taxRate ?? 0;
    if (rate <= 0) {
      continue;
    }
    const lineNet = lineTotal(line) * netFactor;
    taxTotal += Math.round((lineNet * rate) / BASIS_POINTS);
  }

  const total = subtotal - safeDiscount + taxTotal;
  return { subtotal, discount: safeDiscount, taxTotal, total };
}

/** Discount can never be negative or exceed subtotal. */
export function clampDiscount(discount: number, subtotal: number): number {
  const d = Math.max(0, Math.trunc(discount));
  return Math.min(d, Math.max(0, subtotal));
}

/**
 * Cambio a devolver en un pago en efectivo.
 * `tendered` = cash delivered by customer; `total` = amount to collect.
 * Returns 0 if customer paid exact or less (no negative change).
 */
export function changeDue(tendered: number, total: number): number {
  return Math.max(0, Math.trunc(tendered) - Math.trunc(total));
}

/** Suma de pagos registrados, en centavos. */
export function sumPayments(amounts: number[]): number {
  return amounts.reduce((acc, a) => acc + Math.trunc(a), 0);
}

/** Outstanding balance of order (total − paid). 0 if fully paid. */
export function remainingDue(total: number, paidAmounts: number[]): number {
  return Math.max(0, Math.trunc(total) - sumPayments(paidAmounts));
}

/** Suma de propinas registradas, en centavos. */
export function sumTips(tips: number[]): number {
  return tips.reduce((acc, t) => acc + Math.max(0, Math.trunc(t)), 0);
}

/**
 * Equitable split of a total among `parts` diners, in cents.
 * Last receives remainder so sum matches EXACT total
 * (without losing or inventing cents due to rounding).
 */
export function splitEvenly(total: number, parts: number): number[] {
  const n = Math.max(1, Math.trunc(parts));
  const t = Math.max(0, Math.trunc(total));
  const base = Math.floor(t / n);
  const out = Array.from({ length: n }, () => base);
  out[n - 1] = (out[n - 1] ?? base) + (t - base * n);
  return out;
}

/**
 * Payment amount when splitting by items: sum of `line_total` assigned to
 * that guest/payment. Receives selected line totals.
 */
export function splitByItems(lineTotals: number[]): number {
  return lineTotals.reduce((acc, v) => acc + Math.max(0, Math.trunc(v)), 0);
}

/**
 * Does sum of payment amounts (without tip) cover exact total?
 * Split is valid when exact total is paid. Tips
 * are additional and do NOT count toward covering total.
 */
export function isSplitSettled(total: number, paymentAmounts: number[]): boolean {
  return sumPayments(paymentAmounts) >= Math.trunc(total);
}

export interface CashboxReconciliation {
  /** Efectivo esperado = fondo inicial + ventas en efectivo − salidas. */
  expected: number;
  /** Efectivo contado al cierre. */
  counted: number;
  /** Descuadre (counted − expected): positivo sobra, negativo falta. */
  variance: number;
}

/**
 * Arqueo de caja: cuadra el efectivo contado contra el esperado.
 *
 * `cashMovements` are session movements with resolved sign in
 * `signedAmount` (entradas/ventas positivas, salidas/retiros negativos).
 */
export function reconcileCashbox(
  openingFloat: number,
  signedMovements: number[],
  countedTotal: number,
): CashboxReconciliation {
  const expected = Math.trunc(openingFloat) + sumPayments(signedMovements);
  const counted = Math.trunc(countedTotal);
  return { expected, counted, variance: counted - expected };
}

/**
 * Price and name of a product line added on the POS. A selected variant (size)
 * replaces the base price with its own absolute price and renames the line,
 * e.g. "Coke (Large)".
 */
export function resolveProductLine(
  product: { name: string | null; price: number | null },
  variant: { name: string | null; price: number | null } | null,
): { unitPrice: number; lineName: string } {
  if (variant) {
    return {
      unitPrice: variant.price ?? product.price ?? 0,
      lineName: `${product.name} (${variant.name})`,
    };
  }
  return { unitPrice: product.price ?? 0, lineName: product.name ?? "" };
}
